import {
  ApolloCache,
  ApolloClient,
  DefaultContext,
  InMemoryCache,
  MutationFunctionOptions,
  MutationTuple,
  NormalizedCacheObject,
  useApolloClient,
} from '@apollo/client';
import jwt_decode from 'jwt-decode';
import { useCallback } from 'react';
import { useHistory } from 'react-router';
import { useObservableCache } from '../cache/observable-cache-provider';
import { useIntercomLogout } from '../config/intercom';
import { DIRECT_CONNECT_MACHINES_CACHE_KEY } from '../direct-connect/cache/direct-connect-machine-cache';
import { useBeaconRouter } from '../route/route.hooks';
import {
  useActiveWorkspace,
  useGetActiveWorkspaceLazy,
  useWorkspaceHelpers,
} from '../store/workspace/workspace.hooks';
import { logger } from '../util/logger';
import { getPlatform } from '../util/platform';
import { VERSION } from '../version';
import {
  AuthMethod,
  Credentials,
  Exact,
  InitiateAuthFlowMutation,
  InitiateAuthFlowMutationVariables,
  InitiateAuthFlowRequest,
  InitiateAuthFlowResponse,
  LogoutDocument,
  LogoutMutation,
  LogoutMutationVariables,
  RefreshTokenDocument,
  RefreshTokenQuery,
  RefreshTokenQueryVariables,
  SignupMutation,
  SignupMutationVariables,
  SignupRequest,
  Workspace,
  useInitiateAuthFlowMutation,
  useSignupMutation,
} from './graphql/generated';

export interface JwtToken {
  exp: number;
}

export interface JwtProviderResponse {
  token?: string;
  tenantId?: string;
}

export type JwtProvider = () => Promise<JwtProviderResponse | undefined>;

export interface JwtProviderOptions {
  uri: string;
}

type LogoutCallback = () => Promise<void>;

interface GetCredentialsFromWorkspace {
  credentials?: Credentials;
  refreshed?: boolean;
}

export const getRefreshToken = async (
  refreshToken: string,
  client: ApolloClient<NormalizedCacheObject>
): Promise<Credentials | undefined> => {
  // The refresh token api call has been switched back to a query
  // so we can benefit from the dedupe ability built into Apollo.
  // When it was a mutation we noticed that multiple refresh token
  // api calls were going outbound when a token expired causing
  // rate limit errors against the api. If we want to use a mutation
  // in the future we need to ensure only one call is made to the
  // server and the response is shared with everyone
  const response = await client.query<
    RefreshTokenQuery,
    RefreshTokenQueryVariables
  >({
    query: RefreshTokenDocument,
    variables: {
      input: {
        refreshToken,
      },
    },
    fetchPolicy: 'network-only',
  });

  if (response?.data?.refreshToken?.credentials) {
    return response?.data?.refreshToken?.credentials;
  }
};

export const isTokenExpired = (token: string): boolean => {
  const decoded = jwt_decode(token) as JwtToken;

  if (decoded) {
    const timeInSeconds = Date.now() / 1000;
    if (decoded.exp > timeInSeconds) {
      return false;
    }
  }

  return true;
};

export const getCredentialsFromWorkspace = async (
  workspace: Workspace,
  client: ApolloClient<NormalizedCacheObject>
): Promise<GetCredentialsFromWorkspace | undefined> => {
  if (workspace.credentials) {
    let credentials: Credentials | undefined = workspace.credentials;
    let refreshed = false;

    if (isTokenExpired(credentials.idToken)) {
      credentials = await getRefreshToken(credentials.refreshToken, client);
      refreshed = true;
    }

    return {
      credentials,
      refreshed,
    };
  }
};

const refreshCache: Record<string, ApolloClient<NormalizedCacheObject>> = {};

const getSharedRefreshClient = (
  uri: string,
  cache: InMemoryCache
): ApolloClient<NormalizedCacheObject> => {
  if (uri in refreshCache) {
    return refreshCache[uri];
  }

  const refreshClient = new ApolloClient({
    uri,
    cache,
    name: getPlatform(),
    version: VERSION,
  });

  refreshCache[uri] = refreshClient;

  return refreshClient;
};

export const useJwtProvider = (options: JwtProviderOptions): JwtProvider => {
  const { upsertWorkspace } = useWorkspaceHelpers();
  const { navigateToLogout } = useBeaconRouter();
  const getActiveWorkspaceLazy = useGetActiveWorkspaceLazy();

  const jwtProvider: JwtProvider = useCallback(async () => {
    const workspace = await getActiveWorkspaceLazy();

    if (workspace) {
      // we need a new apollo client since the existing will delegate to this
      // function for refreshing. if we use the same client then we will block
      // the in-flight request on the main client. not sure if this needs to be
      // cached or not
      const refreshClient = getSharedRefreshClient(
        options.uri,
        new InMemoryCache()
      );

      const response = await getCredentialsFromWorkspace(
        workspace,
        refreshClient
      );

      if (response?.refreshed) {
        if (response.credentials) {
          await upsertWorkspace({
            ...workspace,
            credentials: response.credentials,
          });
        } else {
          navigateToLogout();
        }
      }

      return {
        token: response?.credentials?.idToken,
        tenantId: workspace.tenant?.id,
      };
    }
  }, [options.uri, upsertWorkspace, navigateToLogout, getActiveWorkspaceLazy]);

  return jwtProvider;
};

export const useLogout = (): LogoutCallback => {
  const client = useApolloClient();
  const intercomLogout = useIntercomLogout();
  const observableCache = useObservableCache();

  const workspace = useActiveWorkspace();

  const performLogout: LogoutCallback = useCallback(async (): Promise<void> => {
    try {
      await intercomLogout();
    } catch (error) {
      // do nothing
    }

    try {
      await client.mutate<LogoutMutation, LogoutMutationVariables>({
        mutation: LogoutDocument,
        fetchPolicy: 'network-only',
        variables: {
          input: {
            refreshToken: workspace?.credentials?.refreshToken,
          },
        },
      });
    } catch (error) {
      // do nothing
    } finally {
      await client.resetStore();

      await observableCache.clear({
        exclude: [DIRECT_CONNECT_MACHINES_CACHE_KEY],
      });
    }
  }, [client, intercomLogout, workspace, observableCache]);

  return performLogout;
};

export const useInitiateAuth = (): MutationTuple<
  InitiateAuthFlowMutation,
  InitiateAuthFlowMutationVariables
> => {
  const gqlResults = useInitiateAuthFlowMutation({
    fetchPolicy: 'network-only',
  });
  const redirectToAuthPage = useRedirectToAuthPage();
  const [initiateAuth] = gqlResults;

  const internalInitiateAuth = useCallback(
    async (
      options?: MutationFunctionOptions<
        InitiateAuthFlowMutation,
        Exact<{ input: InitiateAuthFlowRequest }>,
        DefaultContext,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ApolloCache<any>
      >
    ) => {
      const result = await initiateAuth(options);

      if (options) {
        const { variables } = options;
        const { data } = result;
        if (data?.initiateAuthFlow) {
          const { expiresIn, authMethod, username } = data.initiateAuthFlow;
          logger.info('received data for initiate auth flow', {
            expiresIn,
            authMethod,
            username,
            variables,
          });

          redirectToAuthPage({
            email: variables?.input.email as string,
            expiresIn,
            authMethod,
            username,
          });
        }
      }

      return result;
    },
    [initiateAuth, redirectToAuthPage]
  );

  return [internalInitiateAuth, gqlResults[1]];
};

export const useInitiateSignup = (): MutationTuple<
  SignupMutation,
  SignupMutationVariables
> => {
  const gqlResults = useSignupMutation({
    fetchPolicy: 'network-only',
  });
  const redirectToAuthPage = useRedirectToAuthPage();
  const [signup] = gqlResults;

  const internalInitiateSignup = useCallback(
    async (
      options?: MutationFunctionOptions<
        SignupMutation,
        Exact<{ input: SignupRequest }>,
        DefaultContext,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ApolloCache<any>
      >
    ) => {
      const result = await signup(options);

      if (options) {
        const { variables } = options;
        const { data } = result;
        if (data?.signup) {
          const { expiresIn, authMethod, username } = data.signup;
          logger.info('received data for signup', {
            expiresIn,
            authMethod,
            username,
            variables,
          });

          redirectToAuthPage({
            email: variables?.input.email as string,
            expiresIn,
            authMethod,
            username,
          });
        }
      }

      return result;
    },
    [signup, redirectToAuthPage]
  );

  return [internalInitiateSignup, gqlResults[1]];
};

interface RedirectToAuthPageArgs
  extends Omit<InitiateAuthFlowResponse, '__typename'> {
  email: string;
}

const useRedirectToAuthPage = (): ((args: RedirectToAuthPageArgs) => void) => {
  const { push } = useHistory();
  const { builder } = useBeaconRouter();

  const redirectToAuthPage = useCallback(
    (args: RedirectToAuthPageArgs) => {
      const { email, expiresIn, authMethod, username } = args;

      if (authMethod === AuthMethod.PASSWORD) {
        const params = new URLSearchParams();

        params.set('email', `${email}`);
        params.set('username', `${username}`);

        const pushParams = {
          pathname: builder.getAuthConfirmPasswordRoute(),
          search: params.toString(),
        };

        logger.info('navigating to auth confirm password route', {
          pushParams,
        });

        push(pushParams);
      } else {
        const params = new URLSearchParams();

        params.set('expiresIn', `${expiresIn}`);
        params.set('email', `${email}`);

        const pushParams = {
          pathname: builder.getAuthConfirmRoute(),
          search: params.toString(),
        };

        logger.info('navigating to auth confirm route', {
          pushParams,
        });

        push(pushParams);
      }
    },
    [builder, push]
  );

  return redirectToAuthPage;
};
